import { useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSession } from "next-auth/react";
import { setPendingActions } from "@/store/apps/notifier/notifierDataSlice";

const normalizeRole = (role) => {
  const r = (role || "").toLowerCase();
  if (r.includes("sharer")) return "sharer";
  if (r.includes("seeker")) return "seeker";
  return r;
};

const useActionNotifier = () => {
  const dispatch = useDispatch();
  const { data: session } = useSession();
  const currentUserSub = session?.user?.sub;

  const meetings = useSelector((state) => state.meetings.data || []);
  const { role: currentAppRole } = useSelector((state) => state.user);
  const { pendingActions, reviewPromptShown } = useSelector(
    (state) => state.notifications,
  );

  const getPriorityOrder = useCallback((status) => {
    status = status?.toUpperCase();
    const priorityMap = {
      LAUNCHING: 1,
      ONGOING: 1,
      DRAFT: 2,
      REQUESTED: 3,
      ACCEPTED: 4,
      COMPLETED_NEEDS_REVIEW: 5,
    };
    return priorityMap[status] || 99;
  }, []);

  const checkIfNeedsReview = useCallback(
    (meeting, userSub) => {
      if (meeting.seeker_sub !== userSub) return false;
      if (reviewPromptShown.includes(meeting.id)) return false;

      // ✅ Read directly from session data (no API call!)
      const hasReview = meeting.session?.has_review || false;
      return !hasReview; // Needs review if no review exists
    },
    [reviewPromptShown],
  );

  const findActionableMeetings = useCallback(
    (meetingsList, userSub) => {
      if (!userSub) return [];

      const standardActions = meetingsList
        .filter((meeting) => {
          const status = meeting.status?.toUpperCase();
          const isSeeker = meeting.seeker_sub === userSub;
          const isSharer = meeting.sharer_sub === userSub;

          if (["DRAFT", "ACCEPTED"].includes(status)) {
            return isSeeker;
          } else if (status === "REQUESTED") {
            return isSharer;
          } else if (["LAUNCHING", "ONGOING"].includes(status)) {
            return isSeeker || isSharer;
          }
          return false;
        })
        .map((meeting) => {
          const status = meeting.status?.toUpperCase();
          let requiredAppRole;

          if (["DRAFT", "ACCEPTED"].includes(status)) {
            requiredAppRole = "seeker";
          } else if (status === "REQUESTED") {
            requiredAppRole = "sharer";
          } else {
            requiredAppRole = "both";
          }

          const userRoleInMeeting =
            meeting.seeker_sub === userSub ? "seeker" : "sharer";

          return {
            id: meeting.id,
            status: meeting.status,
            title: meeting.title || "Meeting",
            requiredRole: requiredAppRole,
            userRoleInMeeting: userRoleInMeeting,
            priority: getPriorityOrder(meeting.status),
            meetingData: meeting,
          };
        });

      const completedMeetings = meetingsList
        .filter((m) => {
          const status = m.status?.toUpperCase();
          return status === "COMPLETED" && m.seeker_sub === userSub;
        })
        .sort((a, b) => {
          const timeA = a.status_time?.toDate
            ? a.status_time.toDate()
            : new Date(a.status_time || 0);
          const timeB = b.status_time?.toDate
            ? b.status_time.toDate()
            : new Date(b.status_time || 0);
          return timeB - timeA;
        });

      // ✅ No more async - direct synchronous check!
      const reviewChecks = completedMeetings.map((meeting) => {
        const needsReview = checkIfNeedsReview(meeting, userSub);
        return needsReview ? meeting : null;
      });

      const reviewActions = reviewChecks
        .filter(Boolean)
        .slice(0, 1)
        .map((meeting) => ({
          id: `review_${meeting.id}`,
          status: "COMPLETED_NEEDS_REVIEW",
          title: "Leave a Review",
          requiredRole: "seeker",
          userRoleInMeeting: "seeker",
          priority: getPriorityOrder("COMPLETED_NEEDS_REVIEW"),
          meetingData: meeting,
        }));

      return [...standardActions, ...reviewActions].sort(
        (a, b) => a.priority - b.priority,
      );
    },
    [getPriorityOrder, checkIfNeedsReview],
  );

  useEffect(() => {
    if (!meetings.length || !currentUserSub) return;

    // ✅ Now synchronous - no .then() needed!
    const actionableMeetings = findActionableMeetings(meetings, currentUserSub);
    dispatch(setPendingActions(actionableMeetings));
  }, [meetings, currentUserSub, dispatch, findActionableMeetings]);

  const getNextAction = useCallback(() => {
    return pendingActions[0] || null;
  }, [pendingActions]);

  const getActionsRequiringRoleSwitch = useCallback(() => {
    const normalizedCurrentRole = normalizeRole(currentAppRole);
    const result = pendingActions.filter((action) => {
      const match =
        action.requiredRole !== normalizedCurrentRole &&
        action.requiredRole !== "both";
      return match;
    });
    return result;
  }, [pendingActions, currentAppRole]);

  return useMemo(
    () => ({
      pendingActions,
      getNextAction,
      getActionsRequiringRoleSwitch,
    }),
    [pendingActions, getNextAction, getActionsRequiringRoleSwitch],
  );
};

export default useActionNotifier;
